import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  selfActivateWallet,
  updateOwnProfile,
} from "../../services/UserService";
import { updateAuthUser } from "../../store/actions/Auth/AuthActions";
import { formatKenyanPhoneNumber } from "../../utils/helpers";
import { ROLE_SLUGS } from "../../utils/auth/permissionConstants";
import { getAllPartnerTeams } from "../../services/PartnerService";
import { getExpenseTags } from "../../services/TransactionService";
import {
  isExpenseTagPrerequisiteError,
  isStaleTeamSelectionError,
  prerequisiteMessageFor,
} from "../../utils/prerequisiteErrors";
import PrerequisiteAlert from "./PrerequisiteAlert";

const UserProfile = () => {
  const {
    authUser,
  } = useSelector(state => state.auth);

  const isExecAdmin   = authUser.role_slug === ROLE_SLUGS.EXEC_ADMIN;
  const isSuperAdmin  = authUser.role_slug === ROLE_SLUGS.SUPER_ADMIN;
  const needsWallet   = isSuperAdmin && !authUser.has_wallet;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [viewWalletActivationModal, setViewWalletActivationModal] = useState(false);
  const [walletTeamId, setWalletTeamId] = useState('');
  const [walletActivationTopLevelError, setWalletActivationTopLevelError] = useState(null);
  const [viewEditProfileModal, setViewEditProfileModal] = useState(false);
  const [editProfileErrors, setEditProfileErrors] = useState({});
  const [editProfileData, setEditProfileData] = useState({
    first_name: "",
    last_name: "",
    preferred_name: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    if (authUser && viewEditProfileModal) {
      setEditProfileData({
        first_name: authUser.first_name ?? "",
        last_name: authUser.last_name ?? "",
        preferred_name: authUser.preferred_name ?? "",
        email: authUser.email ?? "",
        phone: authUser.phone ?? "",
      });
      setEditProfileErrors({});
    }
  }, [viewEditProfileModal]);

  const handleEditProfileChange = (e) => {
    const { name, value } = e.target;
    const parsed = name === "phone" ? formatKenyanPhoneNumber(value) : value;
    setEditProfileData((prev) => ({ ...prev, [name]: parsed }));
    setEditProfileErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const updateProfileMutation = useMutation({
    mutationFn: (data) => updateOwnProfile(authUser.partner_id, authUser.id, data),
    onSuccess: (response) => {
      dispatch(updateAuthUser(response.data.data));
      toast.success("Profile updated successfully.");
      setViewEditProfileModal(false);
    },
    onError: (err) => {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors) {
        setEditProfileErrors(
          Object.fromEntries(
            Object.entries(apiErrors).map(([field, messages]) => [field, messages[0]])
          )
        );
      } else {
        toast.error(err?.response?.data?.message || "Failed to update profile. Please try again.");
      }
    },
  });

  const handleEditProfileSubmit = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate(editProfileData);
  };

  const { data: partnerTeamsData, isLoading: partnerTeamsLoading } = useQuery({
    queryKey: ['allPartnerTeams', authUser.partner_id],
    queryFn: () => getAllPartnerTeams(authUser.partner_id),
    enabled: needsWallet,
    staleTime: 5 * 60 * 1000,
  });
  const partnerTeams = partnerTeamsData?.data?.data ?? [];
  const hasTeams = partnerTeams.length > 0;
  const teamsKnown = !partnerTeamsLoading;

  const { data: expenseTagsData, isLoading: expenseTagsLoading } = useQuery({
    queryKey: ['expenseTags', authUser.partner_id, 1],
    queryFn: () => getExpenseTags(authUser.partner_id, 1),
    enabled: needsWallet,
    staleTime: 5 * 60 * 1000,
  });
  const hasExpenseTags = (expenseTagsData?.data?.data?.length ?? 0) > 0;
  const expenseTagsKnown = !expenseTagsLoading;

  const missingExpenseTag = expenseTagsKnown && !hasExpenseTags;
  const missingTeam = expenseTagsKnown && teamsKnown && hasExpenseTags && !hasTeams;
  const prerequisitesReady = expenseTagsKnown && teamsKnown && hasExpenseTags && hasTeams;

  const walletActivationMutation = useMutation({
    mutationFn: () => selfActivateWallet(authUser.partner_id, authUser.id, { team_id: walletTeamId }),
    onSuccess: (response) => {
      dispatch(updateAuthUser(response.data.data));
      // Invalidate queries whose values change when a wallet activates:
      // deferred top-ups may auto-retry into the wallet through the
      // backend hook, the wallet balance reflects the incoming funds,
      // and the solo-user count is unchanged but its cache is stale
      // enough that a fresh read is cheap insurance.
      queryClient.invalidateQueries({ queryKey: ['deferredTopUpsForWalletActivation', authUser.partner_id] });
      queryClient.invalidateQueries({ queryKey: ['walletBalance', authUser.id] });
      queryClient.invalidateQueries({ queryKey: ['partnerHasSoloUser', authUser.partner_id] });
      queryClient.invalidateQueries({ queryKey: ['activePartnerUsersCount', authUser.partner_id] });
      toast.success('Wallet activated successfully.');
      setViewWalletActivationModal(false);
      setWalletTeamId('');
      setWalletActivationTopLevelError(null);
    },
    onError: (err) => {
      if (err?.response?.status === 422) {
        queryClient.invalidateQueries({ queryKey: ['expenseTags', authUser.partner_id] });
        queryClient.invalidateQueries({ queryKey: ['allPartnerTeams', authUser.partner_id] });
      }
      if (isExpenseTagPrerequisiteError(err)) {
        setWalletActivationTopLevelError(
          prerequisiteMessageFor(err, 'Add at least one expense tag before activating a wallet.')
        );
        return;
      }
      if (isStaleTeamSelectionError(err)) {
        setWalletActivationTopLevelError(
          'The team you selected is no longer available. Please pick another.'
        );
        setWalletTeamId('');
        return;
      }
      toast.error(err?.response?.data?.message || 'Failed to activate wallet. Please try again.');
    },
  });

  return (
    <Fragment>
      <div className="row">
        <div className="col-12">
          <div className="card overflow-hidden">
            <div className="text-center p-3 bg-primary">
              <h3 className="mt-3 mb-1 text-white">{authUser.preferred_name}</h3>
              <div className="d-flex justify-content-center flex-wrap gap-2 mt-1">
                {authUser.role && (
                  <span className="badge badge-rounded bg-white text-primary">{authUser.role}</span>
                )}
                {authUser.is_team_reviewer && (
                  <span className="badge badge-rounded bg-white text-primary">Team reviewer</span>
                )}
              </div>
            </div>
            <ul className="list-group list-group-flush">
              <li className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <span className="mb-0">User Status</span>{" "}
                <strong className={`${authUser.status ? "text-success" : "text-danger"}`}>{authUser.status ? "Active" : "Inactive"}</strong>
              </li>
              <li className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <span className="mb-0">Name</span>{" "}
                <strong className="text-muted text-break text-end">{authUser.full_name}</strong>
              </li>
              <li className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <span className="mb-0">Email</span>{" "}
                <strong className="text-muted text-break text-end">{authUser.email}</strong>
              </li>
              <li className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <span className="mb-0">Phone</span>{" "}
                <strong className="text-muted text-break text-end">{authUser.phone || 'Not set'}</strong>
              </li>
              <li className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <span className="mb-0">Role</span>{" "}
                <strong className="text-muted text-break text-end">{authUser.role}</strong>
              </li>
              {authUser.is_team_reviewer && (authUser.reviewed_team_names ?? []).length > 0 && (
                <li className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-wrap">
                  <span className="mb-0">Reviews</span>{" "}
                  <strong className="text-muted text-break text-end">{authUser.reviewed_team_names.join(', ')}</strong>
                </li>
              )}
              {!isExecAdmin && (
                <>
                  <li className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <span className="mb-0">Has Wallet</span>{" "}
                    <strong className={`${authUser.has_wallet ? "text-success" : "text-info"}`}>{authUser.has_wallet ? "Yes" : "No"}</strong>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <span className="mb-0">Partner Vault Status</span>{" "}
                    <strong className={`${authUser.has_active_partner_vault ? "text-success" : "text-danger"}`}>{authUser.has_active_partner_vault ? "Active" : "Inactive"}</strong>
                  </li>
                  {authUser.has_wallet && (
                    <li className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-wrap">
                      <span className="mb-0">Wallet Status</span>{" "}
                      <strong className={`${authUser.has_active_wallet ? "text-success" : "text-danger"}`}>{authUser.has_active_wallet ? "Active" : "Inactive"}</strong>
                    </li>
                  )}
                </>
              )}
              <li className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <span className="mb-0">Date Created</span>{" "}
                <strong className="text-muted text-break text-end">{authUser.created_when}</strong>
              </li>
            </ul>
            <div className="d-flex justify-content-end gap-2 p-3">
              {!isExecAdmin && (
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setViewEditProfileModal(true)}
                >
                  Edit Profile
                </button>
              )}
              {needsWallet && (
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => setViewWalletActivationModal(true)}
                >
                  Activate My Wallet
                </button>
              )}
            </div>
            {!isExecAdmin && (
              <div className="card mt-4">
                <div className="card-header">
                  <h5 className="mb-0">Transaction Limits</h5>
                </div>
                {(() => {
                  const primaryLimits   = authUser.user_transaction_limits ?? []
                  const temporaryLimits = authUser.active_temporary_transaction_limits ?? []
                  const hasAnyLimit     = primaryLimits.length > 0 || temporaryLimits.length > 0

                  if (!hasAnyLimit) {
                    return (
                      <div className="card-body text-muted">
                        This user has no transaction limits.
                      </div>
                    )
                  }

                  return (
                    <ul className="list-group list-group-flush">
                      {temporaryLimits.map((limit) => (
                        <li
                          key={`tmp-${limit.id}`}
                          className="list-group-item"
                          style={{ backgroundColor: 'rgba(255, 193, 7, 0.08)' }}
                        >
                          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                            <div>
                              <span className="badge bg-warning text-dark me-2">Active Override</span>
                              <strong>{limit.description}</strong>
                            </div>
                            <span className="badge bg-warning text-dark">{limit.amount_limit}</span>
                          </div>
                          {limit.expires_on && (
                            <div className="mt-1 small text-warning fw-semibold">
                              Expires: {limit.expires_on}
                            </div>
                          )}
                        </li>
                      ))}
                      {primaryLimits.map((limit) => (
                        <li key={limit.id} className="list-group-item">
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <strong>{limit.description}</strong>
                            <span className="badge bg-primary">{limit.pivot.amount_limit}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )
                })()}
              </div>
            )}

          </div>
        </div>
      </div>

      <Modal
        show={viewWalletActivationModal}
        onHide={() => {
          setViewWalletActivationModal(false);
          setWalletActivationTopLevelError(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Activate My Wallet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Select the team you belong to. Your wallet will be activated immediately.
          </p>
          {missingExpenseTag && (
            <PrerequisiteAlert
              message="Add at least one expense tag before activating a wallet. Every payment spends against a tag, so setup starts there."
              actionLabel="Add expense tag"
              onAction={() => navigate("/admin-details#expense-tags")}
            />
          )}
          {missingTeam && (
            <PrerequisiteAlert
              message="Add at least one team before activating a wallet. Every wallet holder belongs to a team, so create a team first."
              actionLabel="Add team"
              onAction={() => navigate("/admin-details#teams")}
            />
          )}
          {walletActivationTopLevelError && (
            <PrerequisiteAlert message={walletActivationTopLevelError} />
          )}
          <Form.Group>
            <Form.Label>Team</Form.Label>
            <Form.Select
              value={walletTeamId}
              onChange={(e) => setWalletTeamId(e.target.value)}
              disabled={walletActivationMutation.isPending}
            >
              <option value="">-- Select a team --</option>
              {partnerTeams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setViewWalletActivationModal(false);
              setWalletActivationTopLevelError(null);
            }}
            disabled={walletActivationMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={() => {
              setWalletActivationTopLevelError(null);
              walletActivationMutation.mutate();
            }}
            disabled={!walletTeamId || walletActivationMutation.isPending || !prerequisitesReady}
            title={
              missingExpenseTag
                ? "Add an expense tag first."
                : (missingTeam ? "Add a team first." : undefined)
            }
          >
            {walletActivationMutation.isPending && <Spinner animation="border" size="sm" className="me-2" />}
            Activate Wallet
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={viewEditProfileModal} onHide={() => setViewEditProfileModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Profile</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditProfileSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                name="first_name"
                value={editProfileData.first_name}
                onChange={handleEditProfileChange}
                isInvalid={!!editProfileErrors.first_name}
              />
              <Form.Control.Feedback type="invalid">{editProfileErrors.first_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                name="last_name"
                value={editProfileData.last_name}
                onChange={handleEditProfileChange}
                isInvalid={!!editProfileErrors.last_name}
              />
              <Form.Control.Feedback type="invalid">{editProfileErrors.last_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Preferred Name</Form.Label>
              <Form.Control
                name="preferred_name"
                value={editProfileData.preferred_name}
                onChange={handleEditProfileChange}
                isInvalid={!!editProfileErrors.preferred_name}
              />
              <Form.Control.Feedback type="invalid">{editProfileErrors.preferred_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={editProfileData.email}
                onChange={handleEditProfileChange}
                isInvalid={!!editProfileErrors.email}
              />
              <Form.Control.Feedback type="invalid">{editProfileErrors.email}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                name="phone"
                value={editProfileData.phone}
                onChange={handleEditProfileChange}
                isInvalid={!!editProfileErrors.phone}
              />
              <Form.Control.Feedback type="invalid">{editProfileErrors.phone}</Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setViewEditProfileModal(false)} disabled={updateProfileMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={updateProfileMutation.isPending}>
              {updateProfileMutation.isPending && <Spinner animation="border" size="sm" className="me-2" />}
              Save Changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Fragment>
  );
};

export default UserProfile;